import { useWorkingPlatform } from '@repo/platform';
import { useCallback } from 'react';

import { applyPossibleQuery } from '@/__generated__/applyPossibleQuery.graphql';
import { 개인사업자고액환급액기준금액, 법인사업자고액환급액기준금액 } from '@/components/tax-refund/common/config/condition';
import { TAX_REFUND_ERROR } from '@/components/tax-refund/common/error/error';
import { useLookupResultRefundData } from '@/components/tax-refund/lookup/result/LookupResultContext';

export function useApplyPossibleRefundData(data: applyPossibleQuery['response']) {
  const { checkApplyRefund, checkApplyTritxRefund, bznavRefund } = data;
  const { workingPlatform } = useWorkingPlatform();
  const { refundAmount, businessType, refundId, refundStatus, tritxRefundInfo } = useLookupResultRefundData();

  // 정책상 수수료율이 변경되면 같이 수정되어야 하는 값. 하지만 프론트에서 이 값이 쓰인다는 건 에러 상황으로 볼 수 있음.
  const DEFAULT_FEE_RATE = 30;

  const isIncomeAlreadyApplied = refundStatus === 'ApplyPossible' && checkApplyRefund?.errors?.code === TAX_REFUND_ERROR.중복신청;
  const isTritxAlreadyApplied = tritxRefundInfo?.refundStatus === 'ApplyPossible' && checkApplyTritxRefund?.errors?.code === TAX_REFUND_ERROR.중복신청;
  const hasDuplicateApplication = isIncomeAlreadyApplied || isTritxAlreadyApplied;

  return {
    isVIP:
      (businessType === 'corps' ? refundAmount >= 법인사업자고액환급액기준금액 : refundAmount >= 개인사업자고액환급액기준금액) && workingPlatform === 'web',
    email: checkApplyRefund.email || checkApplyTritxRefund?.email || '',
    lastLoginType: checkApplyRefund.lastLoginType || checkApplyTritxRefund?.lastLoginType || '',
    possibleApply: checkApplyRefund.result || checkApplyTritxRefund?.result,
    isAlreadyApplied: hasDuplicateApplication,
    defaultFeeRate: bznavRefund?.user?.[businessType]?.find(({ refundId: id }) => id === refundId)?.defaultFeeRate || DEFAULT_FEE_RATE
  };
}

export const useRefundConversionData = () => {
  const { workingPlatform } = useWorkingPlatform();

  const getConversionData = useCallback(
    (businessType: 'indis' | 'corps', incomeValue?: string | number, incomeRefundStatus?: string, tritxValue?: string | number, tritxRefundStatus?: string) => {
      const incomeAmount = Number(incomeValue ?? 0);
      const tritxAmount = Number(tritxValue ?? 0);
      const isApplyPossible = {
        income: incomeRefundStatus === 'ApplyPossible',
        tritx: tritxRefundStatus === 'ApplyPossible'
      };
      const totalAmount = incomeAmount + tritxAmount;
      const applyPossibleAmount = (isApplyPossible.income ? incomeAmount : 0) + (isApplyPossible.tritx ? tritxAmount : 0);

      const refundType = (() => {
        if (isApplyPossible.income && isApplyPossible.tritx) {
          return 'all';
        } else if (isApplyPossible.income) {
          return 'only-income';
        } else if (isApplyPossible.tritx) {
          return 'only-gains';
        }
        return 'none';
      })();

      const refundGrade = (() => {
        if (isApplyPossible.income || isApplyPossible.tritx) {
          if (
            (businessType === 'corps' ? incomeAmount >= 법인사업자고액환급액기준금액 : incomeAmount >= 개인사업자고액환급액기준금액) &&
            workingPlatform === 'web'
          )
            return 'vip';
          return 'normal';
        } else if (incomeAmount + tritxAmount === 0) {
          return 'zero';
        }
        return 'shortage';
      })();

      const products = (() => {
        const values = [];
        if (incomeAmount > 0) {
          values.push({
            id: 'income',
            name: '종소세',
            quantity: 1,
            price: incomeAmount,
            brand: 'bznav-refund'
          });
        }
        if (tritxAmount > 0) {
          values.push({
            id: 'gains',
            name: '양도세',
            quantity: 1,
            price: tritxAmount,
            brand: 'bznav-refund'
          });
        }

        return values;
      })();

      return { totalAmount, incomeAmount, tritxAmount, applyPossibleAmount, refundType, refundGrade, products };
    },
    [workingPlatform]
  );

  return { getConversionData };
};
